import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, BookOpen, Lock, ExternalLink } from 'lucide-react';
import { useAuth } from 'react-oidc-context';
import {
  getBooksellerBySlug,
  getBookByBooksellerBookId,
  type BooksellerDto,
  type BookDeepLinkDto
} from '../api/booksellersService';

/**
 * Bookseller Deep Link Handler
 *
 * Handles deep links from bookseller sites when user clicks "Read in LearnLeaf"
 * Route: /book/:slug/:booksellerBookId
 *
 * Examples:
 * - /book/vitalsource/VS-BOOK-12345
 * - /book/redshelf/RS-BOOK-67890
 *
 * Flow:
 * 1. Parse bookseller slug and booksellerBookId from URL
 * 2. Get bookseller info by slug
 * 3. Check if user is authenticated
 * 4. If not authenticated → Initiate OAuth with this URL as returnUrl
 * 5. If authenticated → Resolve booksellerBookId to LearnLeaf bookId
 * 6. Check if user has license
 * 7. If has license → Redirect to reader
 * 8. If no license → Show error with instructions
 */
export default function BooksellerDeepLinkPage() {
  const { slug, booksellerBookId } = useParams<{ slug: string; booksellerBookId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  const [status, setStatus] = useState<'loading' | 'authenticating' | 'resolving' | 'no-license' | 'error'>('loading');
  const [bookseller, setBookseller] = useState<BooksellerDto | null>(null);
  const [bookInfo, setBookInfo] = useState<BookDeepLinkDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleDeepLink();
  }, [auth.isAuthenticated]);

  const handleDeepLink = async () => {
    try {
      // Validate URL parameters
      if (!slug || !booksellerBookId) {
        throw new Error('Invalid deep link: Missing bookseller or book ID');
      }

      console.log(`Deep link received: ${slug} / ${booksellerBookId}`);
      setStatus('loading');

      // Step 1: Get bookseller info by slug
      const booksellerData = await getBooksellerBySlug(slug);
      setBookseller(booksellerData);
      console.log(`Bookseller found: ${booksellerData.name}`);

      // Step 2: If not authenticated, kick off server-mediated OAuth flow
      if (!auth.isAuthenticated) {
        setStatus('authenticating');
        const returnUrl = `/book/${slug}/${booksellerBookId}`;
        window.location.href = `/Account/ExternalLogin?provider=${encodeURIComponent(slug)}&returnUrl=${encodeURIComponent(returnUrl)}`;
        return;
      }

      // Step 3: Authenticated — resolve book and route to reader or no-license page
      setStatus('resolving');
      const resolvedBook = await getBookByBooksellerBookId(booksellerBookId, booksellerData.id);
      setBookInfo(resolvedBook);
      console.log(`Book resolved: ${resolvedBook.title}, HasLicense: ${resolvedBook.hasLicense}`);

      if (resolvedBook.hasLicense) {
        console.log(`Redirecting to reader: /read/${resolvedBook.bookId}`);
        navigate(`/read/${resolvedBook.bookId}`, { replace: true });
      } else {
        setStatus('no-license');
      }
    } catch (err: any) {
      console.error('Deep link error:', err);
      setStatus('error');
      setError(
        err?.response?.data?.error?.message ||
        err.message ||
        'Failed to open book. Please try again or contact support.'
      );
    }
  };

  const handleContactSupport = () => {
    // TODO: Navigate to support page or open contact modal
    console.log('Contact support clicked');
  };

  const handleGoToLibrary = () => {
    navigate('/my-library');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-neutral-200">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2">
                  Opening Book...
                </h2>
                <p className="text-neutral-600">
                  Please wait while we prepare your book
                </p>
              </div>
              {bookseller && (
                <p className="text-sm text-neutral-500">
                  Loading from {bookseller.name}
                </p>
              )}
            </div>
          )}

          {/* Authenticating State */}
          {status === 'authenticating' && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-secondary-600 animate-spin" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2">
                  Signing You In...
                </h2>
                <p className="text-neutral-600">
                  Redirecting to {bookseller?.name} for authentication
                </p>
              </div>
              <p className="text-sm text-neutral-500">
                You'll be returned to this book after signing in
              </p>
            </div>
          )}

          {/* Resolving State */}
          {status === 'resolving' && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2">
                  Loading Book...
                </h2>
                <p className="text-neutral-600">
                  Checking your access permissions
                </p>
              </div>
            </div>
          )}

          {/* No License State */}
          {status === 'no-license' && bookInfo && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center">
                  <Lock className="w-8 h-8 text-warning-600" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2">
                  Access Required
                </h2>
                <p className="text-neutral-600 mb-4">
                  You don't have access to this book yet
                </p>

                {/* Book Info */}
                <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg text-left space-y-2">
                  <div className="flex items-start gap-3">
                    {bookInfo.coverImageUrl ? (
                      <img
                        src={bookInfo.coverImageUrl}
                        alt={bookInfo.title}
                        className="w-16 h-24 object-cover rounded shadow-sm"
                      />
                    ) : (
                      <div className="w-16 h-24 bg-gradient-to-br from-primary-400 to-secondary-400 rounded flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-neutral-900 line-clamp-2">
                        {bookInfo.title}
                      </h3>
                      <p className="text-sm text-neutral-600 line-clamp-1">
                        {bookInfo.author}
                      </p>
                      <p className="text-xs text-neutral-500 mt-1">
                        ISBN: {bookInfo.isbn}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-neutral-600 space-y-2">
                  <p>
                    This book is available through <strong>{bookInfo.booksellerName}</strong>,
                    but it hasn't been added to your LearnLeaf library yet.
                  </p>
                  <p>
                    Please contact your institution or bookseller to request access.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGoToLibrary}
                  className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                  style={{ minHeight: '44px' }}
                >
                  Go to My Library
                </button>
                {bookseller && (
                  <a
                    href={`https://${bookseller.slug}.com`} // TODO: Use actual bookseller website URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-neutral-700 border-2 border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-semibold"
                    style={{ minHeight: '44px' }}
                  >
                    Visit {bookseller.name}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-error-600" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2">
                  Unable to Open Book
                </h2>
                <p className="text-neutral-600 mb-4">
                  We encountered an error while trying to open this book
                </p>
                {error && (
                  <div className="p-4 bg-error-50 border border-error-200 rounded-lg text-sm text-error-800 text-left">
                    {error}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                  style={{ minHeight: '44px' }}
                >
                  Try Again
                </button>
                <button
                  onClick={handleGoToLibrary}
                  className="w-full px-6 py-3 bg-white text-neutral-700 border-2 border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-semibold"
                  style={{ minHeight: '44px' }}
                >
                  Go to My Library
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
